// Fetch de BOOT do snapshot do catálogo — a TAREFA, separada do PIXEL.
//
// Antes isto morava dentro do `BandwidthProbeOverlay`: o ciclo de vida do DADO
// estava amarrado ao ciclo de vida de uma cortina full-screen que:
//   1. chamava `setFocus('bw-skip')` no mount e ROUBAVA o foco da Home;
//   2. segurava a TV por um piso de 4,0s cravado no código
//      (MIN_VISIBLE_MS 1500 + DONE_DISPLAY_MS 2500);
//   3. ao desmontar, deixava `[data-focused]` órfão — `bw-skip` era folha direta
//      do `SN:ROOT` (sem FocusContext ao redor) e o Norigin não auto-restaura
//      folha do ROOT. D-pad morto.
// Medido na LG Chr53 do Fernando (v2.29.0): Home montava em ~3,3-3,5s e o foco
// só chegava no menu em 5,0-6,2s.
//
// Regra que este módulo materializa (desenho do ux-tv): **do primeiro paint em
// diante, o foco pertence à tela; nada que seja trabalho de fundo pode
// segurá-lo, nem por 1 frame.** Aqui NÃO existe `setFocus`, não existe
// `Focusable`, não existe import de foco. Por construção.
//
// O que este módulo NÃO mudou (de propósito):
// - A MEDIÇÃO de banda continua onde sempre esteve: `catalog-snapshot.ts`
//   calcula `mbps` pelos bytes/tempo do próprio download e `catalog-store.ts`
//   persiste + emite a telemetria `snapshot_write`. Enquanto o download passar
//   por `loadSnapshot` → `catalogStore.set`, zero telemetria é perdida. O que
//   morreu foi a APRESENTAÇÃO ("Excelente · 87 Mbps · 1234 títulos" segurando a
//   tela por 2,5s), redundante com Configurações (que hidrata do próprio
//   snapshot via `catalogStore.get`).
// - O gatilho: continua 1× por transição de `isAuthed` (não re-dispara em troca
//   de provider sem logout — mesma semântica do efeito antigo em providers.tsx).
//
// O que GANHOU: `catalog_revalidated{source:'boot'}`. O fetch de boot era
// invisível na telemetria QoE (só o `snapshot_write` do catalog-store aparecia).
// Sem ele não dá pra fechar a conta do achado do ux-tv ("o overlay reaparece em
// TODO boot" = suspeita de gravação falhando no device) lendo só o device.
//
// Padrão de store: module-level + Set de listeners + useSyncExternalStore,
// igual a `trial-block-store.ts` / `detail-overlay-store.ts`. Os seletores
// devolvem PRIMITIVOS (nunca objeto novo) — senão o useSyncExternalStore entra
// em loop, e o bail-out por Object.is é justamente o que impede o tick de
// progresso (10fps) de re-renderizar a árvore inteira do app a partir do
// AppProviders: quem lê `bytes` é só a folha do indicador.

import { useSyncExternalStore } from 'react'

import { loadSnapshot, type LoadSnapshotOptions } from './catalog-snapshot'
import { catalogStore } from './catalog-store'
import { orFallbackAfter } from './lib/deadline'
import { track } from './qoe'
import { useSessionStore } from './store'

/**
 * Fase do fetch de boot.
 *
 * - `idle`      — não começou (deslogado / sem provider) ou foi resetado.
 * - `checking`  — lendo o meta do IDB pra decidir se precisa baixar.
 * - `connecting`— fetch disparado, nenhum byte ainda.
 * - `downloading`— bytes chegando.
 * - `done`      — acabou (cache fresco, sucesso, erro ou IDB indisponível).
 *
 * `done` é TERMINAL e significa "o fetch de boot não está mais em voo" — é o
 * que libera o `useCatalogRefresh`. NÃO significa "deu certo".
 */
export type BootSnapshotPhase =
  | 'idle'
  | 'checking'
  | 'connecting'
  | 'downloading'
  | 'done'

/** Throttle do progresso: 100ms = ~10fps de updates. Suficiente pra barra
 *  animada sem thrash de re-render em conexão rápida. */
const PROGRESS_THROTTLE_MS = 100

/** Vlow/low não recebem `onProgress` (custo de re-render). Pra o rótulo não
 *  ficar preso em "Conectando", avança pra `downloading` após o handshake
 *  típico. Não é exato — é determinístico, e nesses tiers não há barra. */
const LOW_TIER_DOWNLOADING_MS = 300

/**
 * PRAZO DURO do fetch de boot. Estourou, a fase vai pra `done` — aconteça o que
 * acontecer, sem exceção e sem condição.
 *
 * Este é o item que transforma "em voo" num estado com TERMINAÇÃO GARANTIDA, e
 * sem ele a camada de cima (o gate perguntar "em voo?" em vez de "!done?") não
 * basta: um `await` pendurado em `checking`/`connecting` deixa "em voo" ligado
 * pra sempre e o gate fecha do mesmo jeito. Foi assim que o catálogo sumiu.
 *
 * O prazo NÃO CANCELA o download. Ele só solta o gate: o `loadSnapshot` em voo
 * segue vivo, ainda grava no IDB e ainda emite `catalog_revalidated` quando (e
 * se) terminar. O pior caso vira "o catálogo demorou", nunca "o catálogo nunca
 * vem".
 *
 * 45s: acima de qualquer boot saudável com folga (os prazos por-`await` do
 * `catalog-snapshot.ts` já cortam rede morta em 15-20s), e abaixo do ponto em
 * que o usuário desiste do aparelho. Um download LEGÍTIMO e lento que ultrapasse
 * isto paga, no máximo, uma revalidação concorrente — troca obviamente boa
 * contra uma TV sem catálogo.
 */
const BOOT_DEADLINE_MS = 45_000

/** Leitura de meta no IDB: a transação pode pendurar no evento `blocked` (quota
 *  apertada em Tizen 2017 é o caso real). Sem resposta em 5s tratamos como "não
 *  tem meta" — a decisão seguinte (baixar) é a MESMA que tomaríamos com meta
 *  ausente, então o fallback não mente. Recupera em 5s em vez de esperar os 45s
 *  do prazo duro. */
const META_READ_TIMEOUT_MS = 5_000

let phase: BootSnapshotPhase = 'idle'
let bytes = 0
/** Geração do run em voo. Incrementa em todo start/reset — callbacks e
 *  continuações de promise de um run velho viram no-op (logout no meio do
 *  download não pode ressuscitar a fase). */
let runId = 0
/** Chave do snapshot cujo boot já foi disparado. Guarda de idempotência
 *  (StrictMode roda o effect 2×; HMR remonta o AppProviders). */
let activeKey: string | null = null

const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function setPhase(next: BootSnapshotPhase): void {
  if (phase === next) return
  // `done` é TERMINAL — e isso é uma REGRA, não uma descrição. Depois que o
  // prazo duro dispara (timer de tiro único: ele não volta), qualquer coisa que
  // devolvesse a fase pra `downloading` re-fecharia o gate do catálogo, agora
  // sem prazo nenhum cobrindo. Seria reabrir parcialmente o bug que esta
  // entrega conserta. Só `resetBootSnapshot` (que bumpa `runId`) sai de `done`,
  // e ele vai pra `idle`.
  if (phase === 'done' && next !== 'idle') return
  phase = next
  emit()
}

function devLog(...args: unknown[]): void {
  if (import.meta.env.DEV) console.warn(...args)
}

/** Chave do snapshot da sessão atual. Intersecção estrita das duas versões que
 *  existiam antes (providers.tsx usava `direct:${listName}`, o overlay exigia
 *  `listName` não-vazio): provider code, ou `direct:<lista>` com nome não-vazio,
 *  ou nada. Uma chave só pro `getMeta` e pro `loadSnapshot` — eram a mesma
 *  string, agora é literalmente a mesma variável. */
function snapshotKeyFromSession(): string | null {
  const s = useSessionStore.getState()
  if (s.providerCode) return s.providerCode
  const listName = s.direct?.listName
  if (listName) return `direct:${listName}`
  return null
}

function trackBoot(
  providerScope: string,
  result: '304' | '200' | 'cache' | 'error',
  startedAt: number,
): void {
  track({
    type: 'catalog_revalidated',
    ts: Math.floor(Date.now() / 1000),
    source: 'boot',
    result,
    providerScope,
    elapsedMs: Math.round(performance.now() - startedAt),
  })
}

/**
 * Arma o prazo duro da geração `myRun`. O callback é INCONDICIONAL de propósito
 * — não pergunta "deu erro?", não pergunta "a rede voltou?": se a fase ainda é
 * transitória quando o prazo estoura, ela vira terminal. Qualquer condição aqui
 * reabriria a porta pro estado sem terminação garantida.
 *
 * A guarda por geração (`myRun !== runId`) não é condição de negócio: é o mesmo
 * anti-zumbi que o resto do módulo usa pra um run velho não ressuscitar fase de
 * um run novo (logout no meio do download).
 */
function armBootDeadline(myRun: number): { clear: () => void } {
  const timer = setTimeout(() => {
    if (myRun !== runId) return
    if (phase === 'done' || phase === 'idle') return
    devLog(
      `[snapshot] prazo de boot (${BOOT_DEADLINE_MS}ms) estourou em "${phase}" — ` +
        'liberando o gate do catálogo; o download em voo segue e ainda grava',
    )
    setPhase('done')
  }, BOOT_DEADLINE_MS)
  // Simetria com o `makeNetDeadline`: no browser é no-op, mas em Node (suíte) um
  // prazo de 45s armado seguraria o processo até o fim do prazo se alguém
  // esquecesse de desarmá-lo. Prazo não deve poder atrasar o encerramento de
  // quem o armou.
  const t = timer as unknown as { unref?: () => void }
  if (typeof t.unref === 'function') t.unref()
  return {
    clear: () => clearTimeout(timer),
  }
}

async function run(key: string, myRun: number): Promise<void> {
  // O prazo cobre todo o corpo abaixo — mas é um timer de TIRO ÚNICO, e vale
  // dizer o que ele NÃO é: ele não re-arma e não vigia nada depois de disparar.
  // Quem garante que a fase não volta atrás é a regra de terminal do
  // `setPhase`, não este timer. (Um `await` novo adicionado aqui herda o prazo;
  // um caminho novo que ESCREVA fase fora do `setPhase`, não.)
  const deadline = armBootDeadline(myRun)
  try {
    await runBody(key, myRun)
  } finally {
    deadline.clear()
  }
}

async function runBody(key: string, myRun: number): Promise<void> {
  // ── 1. Já tem snapshot fresco? Nem baixa. ────────────────────────────────
  // getMeta, não get: aqui só perguntamos "tem snapshot fresco?". Ler o record
  // inteiro desserializava o catálogo todo (5-15MB) no BOOT por causa de um
  // único inteiro (storedAt).
  try {
    const meta = await orFallbackAfter(catalogStore.getMeta(key), META_READ_TIMEOUT_MS, null)
    if (myRun !== runId) return
    if (meta && !catalogStore.isStale(meta)) {
      devLog('[snapshot] cache fresco — sem fetch de boot')
      setPhase('done')
      return
    }
  } catch (err) {
    // IDB indisponível. Mesmo desfecho de sempre (o `.then` sem `.catch` do
    // providers.tsx antigo simplesmente não ligava o overlay): não baixamos no
    // boot. Quem cobre é o `revalidate('session-start')` do useCatalogRefresh,
    // 5s depois — e agora a rejeição não fica solta como unhandled.
    if (myRun !== runId) return
    devLog('[snapshot] meta indisponível — pulando fetch de boot', err)
    setPhase('done')
    return
  }

  // ── 2. Baixa (e mede banda pelos bytes, dentro do loadSnapshot). ────────
  setPhase('connecting')

  const tier =
    (typeof document !== 'undefined' ? document.documentElement.dataset['tier'] : null) ??
    'mid'
  const lowTier = tier === 'vlow' || tier === 'low'

  let lastProgressAt = 0
  const optsLoad: LoadSnapshotOptions = {}
  if (!lowTier) {
    // `exactOptionalPropertyTypes`: atribuição condicional, nunca
    // `{ onProgress: undefined }`.
    optsLoad.onProgress = (b: number) => {
      if (myRun !== runId) return
      // Terminal é terminal: um chunk que chega DEPOIS do prazo duro não
      // ressuscita a fase. Sem esta linha, um único byte atrasado devolvia
      // `downloading`, o gate voltava a `null` e ficava assim pelo resto do
      // download — sem prazo cobrindo, porque o timer já tinha disparado.
      // (O `setPhase` também recusa a transição; aqui a guarda evita até o
      // `emit`, já que a pílula nem está montada em `done`.)
      if (phase === 'done') return
      const now = performance.now()
      if (now - lastProgressAt < PROGRESS_THROTTLE_MS) return
      lastProgressAt = now
      bytes = b
      // Atribuição direta + 1 `emit` (em vez de `setPhase` + `emit`) pra não
      // emitir duas vezes no tick da transição: isto roda a ~10fps.
      phase = 'downloading'
      emit()
    }
  }

  const lowTierTimer = lowTier
    ? setTimeout(() => {
        if (myRun !== runId) return
        if (phase === 'connecting') setPhase('downloading')
      }, LOW_TIER_DOWNLOADING_MS)
    : null

  const startedAt = performance.now()
  try {
    const r = await loadSnapshot(key, optsLoad)
    if (myRun !== runId) return
    trackBoot(
      key,
      r.source === 'not-modified' ? '304' : r.source === 'fetch' ? '200' : 'cache',
      startedAt,
    )
  } catch (err) {
    if (myRun !== runId) return
    devLog('[snapshot] erro no fetch de boot', err)
    trackBoot(key, 'error', startedAt)
  } finally {
    if (lowTierTimer !== null) clearTimeout(lowTierTimer)
    // Roda mesmo nos `return` de run velho — daí a re-checagem.
    if (myRun === runId) setPhase('done')
  }
}

/**
 * Dispara o fetch de boot do snapshot. Idempotente por chave: chamar de novo
 * com a mesma chave enquanto ela já foi disparada é no-op (StrictMode / HMR).
 *
 * Não bloqueia render, não toca foco, não cobre a tela.
 */
export function startBootSnapshot(): void {
  const key = snapshotKeyFromSession()
  if (!key) {
    resetBootSnapshot()
    return
  }
  if (activeKey === key && phase !== 'idle') return
  activeKey = key
  bytes = 0
  const myRun = ++runId
  setPhase('checking')
  void run(key, myRun)
}

/** Zera o estado (logout). Invalida o run em voo via geração. */
export function resetBootSnapshot(): void {
  runId += 1
  activeKey = null
  bytes = 0
  setPhase('idle')
}

export function getBootSnapshotPhase(): BootSnapshotPhase {
  return phase
}

export function getBootSnapshotBytes(): number {
  return bytes
}

/**
 * `true` enquanto o fetch de boot está EM VOO (`checking`/`connecting`/
 * `downloading`).
 *
 * NÃO é "`phase === 'done'` negado", e a diferença é o bug: `idle` também não é
 * `done`, mas `idle` é TERMINAL na prática — `startBootSnapshot` cai em
 * `resetBootSnapshot()` e VOLTA quando não há chave de snapshot (sessão sem
 * `providerCode` e sem `direct.listName`). Quem guardasse UI em `!done` deixaria
 * essa sessão presa pra sempre no estado transitório. Aqui a pergunta é
 * afirmativa — "tem download de boot em voo?" — e a resposta é `false` tanto no
 * fim quanto no caso "nunca teve".
 *
 * ⚠️ Este é o ÚNICO seletor booleano de fase exportado, e é de propósito. Existiu
 * um `useBootSnapshotDone()` irmão; o `providers.tsx` guardou o gate do catálogo
 * em `!bootSnapshotDone` e o app subiu SEM CATÁLOGO e sem recuperação possível
 * (nem tick de 5s, nem poll de 30min, nem refresh de standby), porque o effect
 * de boot tem deps `[isAuthed]` e não re-roda. O hook foi REMOVIDO em vez de
 * documentado de novo: o aviso já existia aqui, logo acima da linha que o
 * cometeu. Não reintroduza um seletor de "terminou com sucesso" pra guardar
 * carga de dados — se precisar da fase exata, use `useBootSnapshotPhase`.
 */
function getBootSnapshotInFlight(): boolean {
  return phase === 'checking' || phase === 'connecting' || phase === 'downloading'
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function useBootSnapshotPhase(): BootSnapshotPhase {
  return useSyncExternalStore(subscribe, getBootSnapshotPhase, getBootSnapshotPhase)
}

export function useBootSnapshotBytes(): number {
  return useSyncExternalStore(subscribe, getBootSnapshotBytes, getBootSnapshotBytes)
}

/** BOOLEANO, não a fase. Quem só precisa saber "ainda está baixando?"
 *  (a Home, pra não afirmar que está vazia) assina isto e re-renderiza 2x no
 *  boot inteiro, em vez dos ~10/s que `useBootSnapshotPhase` + `bytes` custam
 *  na folha do indicador. */
export function useBootSnapshotInFlight(): boolean {
  return useSyncExternalStore(subscribe, getBootSnapshotInFlight, getBootSnapshotInFlight)
}
